import { MdCheckBox, MdIndeterminateCheckBox } from 'react-icons/md';

import type { IExtendedStyleProperties } from '@themes/types/extended';
import type { Optional } from '@types';

import { Box, type CheckboxProps } from '@mui/material';
import type { Theme } from '@mui/material/styles';

import { getColors } from '@themes/utils/getColors';

function getColorStyle({ color, theme }: IExtendedStyleProperties) {
  const colors = getColors(theme, color);
  const { dark, main, lighter } = colors;

  return {
    '&.Mui-focusVisible': {
      outlineOffset: -4,
      outline: `2px solid ${dark}`,
    },
    '&:hover': {
      backgroundColor: lighter,
      '& .icon': {
        borderColor: main,
      },
    },
  };
}

interface ICheckboxSizeProperties {
  size: number;
  fontSize: number;
}

function getSizeStyle(
  size?: Optional<CheckboxProps['size']>
): ICheckboxSizeProperties {
  switch (size) {
    case 'small': {
      return { size: 16, fontSize: 1 };
    }
    case 'large': {
      return { size: 24, fontSize: 1.6 };
    }
    default: {
      return { size: 20, fontSize: 1.35 };
    }
  }
}

function checkboxStyle(size?: Optional<CheckboxProps['size']>) {
  const sizes: ICheckboxSizeProperties = getSizeStyle(size);

  return {
    '& .icon': {
      width: sizes.size,
      height: sizes.size,
      '& .filled': {
        top: '50%',
        left: '50%',
        position: 'absolute',
        fontSize: `${sizes.fontSize}rem`,
        transform: 'translate(-50%, -50%)',
      },
    },
  };
}

export default function Checkbox(theme: Theme) {
  const { palette } = theme;

  return {
    MuiCheckbox: {
      styleOverrides: {
        colorInfo: getColorStyle({ theme, color: 'info' }),
        colorError: getColorStyle({ theme, color: 'error' }),
        colorWarning: getColorStyle({ theme, color: 'warning' }),
        colorSuccess: getColorStyle({ theme, color: 'success' }),
        colorPrimary: getColorStyle({ theme, color: 'primary' }),
        colorSecondary: getColorStyle({ theme, color: 'secondary' }),
        root: {
          borderRadius: 0,
          color: palette.secondary[300],
          '&.size-large': {
            ...checkboxStyle('large'),
          },
          '&.size-small': {
            ...checkboxStyle('small'),
          },
          '&.size-medium': {
            ...checkboxStyle('medium'),
          },
        },
      },
      defaultProps: {
        className: 'size-small',
        icon: (
          <Box
            sx={{
              width: 16,
              height: 16,
              borderRadius: 0.25,
              border: '1px solid',
              borderColor: 'inherit',
            }}
            className="icon"
          />
        ),
        checkedIcon: (
          <Box
            sx={{
              width: 16,
              height: 16,
              borderRadius: 0.25,
              border: '1px solid',
              position: 'relative',
              borderColor: 'inherit',
            }}
            className="icon"
          >
            <MdCheckBox className="filled" style={{ position: 'absolute' }} />
          </Box>
        ),
        indeterminateIcon: (
          <Box
            sx={{
              width: 16,
              height: 16,
              borderRadius: 0.25,
              border: '1px solid',
              position: 'relative',
              borderColor: 'inherit',
            }}
            className="icon"
          >
            <MdIndeterminateCheckBox
              className="filled"
              style={{ position: 'absolute' }}
            />
          </Box>
        ),
      },
    },
  };
}
